import { DOCUMENT, TitleCasePipe } from '@angular/common';
import { Component, inject, input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, Subject, takeUntil } from 'rxjs';

import { MenuItem, PrimeTemplate } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { Menubar } from 'primeng/menubar';
import { Tooltip } from 'primeng/tooltip';

import { AuthenticatorService } from '../../authentication/service/authenticatorService';
import { TenantService } from '../../authentication/service/tenantService';
import { selectUserProfile, selectUserRole } from '../../authentication/state/selector';
import { UserRole } from '../enum/UserRoles';
import { LogoComponent } from '../logo/logo.component';
import { NotificationBellComponent } from '../notifications/notification-bell/notification-bell.component';
import { CustomerDocsLauncherComponent } from '../customer-docs/customer-docs-launcher.component';

@Component({
  selector: 'app-top-navbar',
  templateUrl: './top-navbar.component.html',
  styleUrls: ['./top-navbar.component.css'],
  imports: [
    Menubar,
    LogoComponent,
    PrimeTemplate,
    Tooltip,
    TitleCasePipe,
    Menu,
    NotificationBellComponent,
    CustomerDocsLauncherComponent,
  ],
})
export class TopNavbarComponent implements OnInit, OnDestroy {
  private readonly authenticationService = inject(AuthenticatorService);
  private readonly tenantService = inject(TenantService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);

  private readonly destroy$ = new Subject<void>();

  readonly publicLegal = input(false);

  profileMenuItems: MenuItem[] | undefined;
  profileMenuOpen = false;

  showFeedbackWidget = false;
  showCustomerDocs = false;
  feedbackPortalId: string | null = null;

  username = '';
  displayName = '';
  role = '';

  private firstName: string | null = null;
  private lastName: string | null = null;

  showNameTooltip = false;
  tooltipText = '';

  private static readonly NAME_LIMIT = 40;
  // Other roles (ops, erp) are refused by the customer docs endpoint.
  private static readonly DOCS_ROLES: ReadonlySet<UserRole> = new Set([
    UserRole.ADMIN,
    UserRole.STANDARD,
    UserRole.SUPPLIER,
  ]);

  constructor(...args: unknown[]);
  constructor() {}

  ngOnInit(): void {
    if (this.publicLegal()) {
      return;
    }

    combineLatest([this.store.select(selectUserRole), this.tenantService.getFeedbackConfig()])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([userRole, feedbackConfig]) => {
        const isSupplier = userRole === UserRole.SUPPLIER;
        this.showFeedbackWidget = feedbackConfig.enabled && !isSupplier;
        this.feedbackPortalId = feedbackConfig.portalId;
        if (this.showFeedbackWidget && feedbackConfig.portalId) {
          this.loadProdPadScript();
        }
      });

    combineLatest([this.store.select(selectUserRole), this.store.select(selectUserProfile)])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([userRole, ownUser]) => {
        this.role = userRole ?? '';
        this.showCustomerDocs = TopNavbarComponent.DOCS_ROLES.has(userRole as UserRole);

        this.firstName = ownUser?.firstName ?? null;
        this.lastName = ownUser?.lastName ?? null;

        this.username = this.firstName && this.lastName ? `${this.firstName} ${this.lastName}` : ownUser?.email ?? '';

        const parts = [this.firstName?.trim(), this.lastName?.trim()].filter(Boolean) as string[];
        const fullTooltip = parts.length ? parts.join('\n\n') : this.username;

        if (this.username.length > TopNavbarComponent.NAME_LIMIT) {
          this.displayName = this.username.slice(0, TopNavbarComponent.NAME_LIMIT).trimEnd() + '…';
          this.showNameTooltip = true;
          this.tooltipText = fullTooltip;
        } else {
          this.displayName = this.username;
          this.showNameTooltip = false;
          this.tooltipText = '';
        }

        this.profileMenuItems = [
          {
            label: 'My profile',
            icon: 'pi pi-user',
            command: () => {
              void this.router.navigate(['/my-profile']);
            },
          },
          {
            separator: true,
          },
          {
            label: 'Logout',
            icon: 'pi pi-sign-out',
            command: () => this.authenticationService.logout(),
          },
        ];
      });
  }

  private loadProdPadScript(): void {
    const scriptId = 'prodpad-cfiwjs';
    if (this.document.getElementById(scriptId)) return;
    setTimeout(() => {
      const script = this.document.createElement('script');
      script.id = scriptId;
      script.src = 'https://widget.prodpad.com/customer_feedback_portal_app/inapp_widget/sdk.js';
      script.async = true;
      this.document.head.appendChild(script);
    }, 0);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  goToLogin(): void {
    void this.router.navigate(['/login']);
  }
}
